/**
 * Global Zustand store for voice recording and playback coordination.
 *
 * This store ensures mutual exclusion between recording and playback:
 * - Starting a recording stops any active playback.
 * - Starting playback stops any active recording.
 * - Only one voice message can play at a time across all chats.
 *
 * The store is consumed by:
 * - `useVoiceRecorder` hook (recording lifecycle)
 * - `useVoicePlayer` hook (playback lifecycle)
 */

import { create } from "zustand";

import type { RecordingStatus, PlayingVoice } from "./types";

/** Playback status within the global store (narrower than types.ts — no "loading") */
type StorePlaybackStatus = "idle" | "playing" | "paused";

/** Extends PlayingVoice with a playback status for store tracking */
interface PlayingVoiceMessage extends PlayingVoice {
  status: StorePlaybackStatus;
}

/** Read-only state slice for voice recording and playback */
interface VoiceState {
  /** The chat ID that currently owns the recording session, or null */
  recordingChatId: string | null;

  /** Current recording lifecycle status */
  recordingStatus: RecordingStatus;

  /** The voice message currently playing globally, or null */
  playingVoice: PlayingVoiceMessage | null;
}

/** Actions for mutating voice state */
interface VoiceActions {
  /** Begin a recording session for the given chat (stops any active playback) */
  startRecordingSession: (chatId: string) => void;

  /** Update only the recording status (e.g. idle → requesting) */
  setRecordingStatus: (status: RecordingStatus) => void;

  /** End the recording session and reset to idle */
  endRecordingSession: () => void;

  /** Start playing a voice message (stops any active recording) */
  startPlayback: (chatId: string, messageId: string) => void;

  /** Pause the currently playing voice message */
  pausePlayback: () => void;

  /** Resume the currently paused voice message */
  resumePlayback: () => void;

  /** Stop playback and clear the playing voice reference */
  stopPlayback: () => void;
}

const initialState: VoiceState = {
  recordingChatId: null,
  recordingStatus: "idle",
  playingVoice: null,
};

export const useVoiceStore = create<VoiceState & VoiceActions>((set, get) => ({
  ...initialState,

  startRecordingSession: (chatId) => {
    // Stop any active playback first
    const { playingVoice } = get();
    if (playingVoice) {
      set({ playingVoice: null });
    }
    set({ recordingChatId: chatId, recordingStatus: "recording" });
  },

  setRecordingStatus: (status) => set({ recordingStatus: status }),

  endRecordingSession: () =>
    set({ recordingChatId: null, recordingStatus: "idle" }),

  startPlayback: (chatId, messageId) => {
    // Stop active recording first
    const { recordingChatId } = get();
    if (recordingChatId) {
      set({ recordingChatId: null, recordingStatus: "idle" });
    }
    set({ playingVoice: { chatId, messageId, status: "playing" } });
  },

  pausePlayback: () =>
    set((s) =>
      s.playingVoice
        ? { playingVoice: { ...s.playingVoice, status: "paused" } }
        : s,
    ),

  resumePlayback: () =>
    set((s) =>
      s.playingVoice
        ? { playingVoice: { ...s.playingVoice, status: "playing" } }
        : s,
    ),

  stopPlayback: () => set({ playingVoice: null }),
}));
